import React from 'react';
import BaseTable from '@app/components/common/Table/BaseTable';
import { CustomerModel } from '@app/interfaces/customer';
import { ColumnsType } from 'antd/lib/table';
import { Space, Input, Button, Tag, message } from 'antd';
import { usePermissions } from '@app/hooks/usePermissions';
import CustomerPointsSummary from '../components/CustomerPointsSummary';
import RecalculatePointsModal from '../components/RecalculatePointsModal';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { formatDateTime } from '@app/utils/utils';
import NumberFormatRender from '@app/components/common/NumberFormatRender';
import { PointTran } from '@app/interfaces/pointTran';
import { getCustomerPointsHistoryApi } from '@app/api/customer.api';
import { DayjsDatePicker } from '@app/components/common/pickers/DayjsDatePicker';
import dayjs from 'dayjs';
import { dayjsPresetRanges } from '@app/components/common/pickers/datePresets';
import { API_DATE_FORMAT, DATE_FORMAT } from '@app/constants/dateFormats';

interface CustomerInfoProps {
  record: CustomerModel;
  /** Có = trang chi tiết khách, cho tính lại điểm thiếu rồi gọi lại để tải số mới. */
  onPointsChanged?: () => void;
}

const PointsHistory: React.FC<CustomerInfoProps> = ({ record, onPointsChanged }) => {
  // Popup mở từ form đơn nhận khách từ ô tìm kiếm (không kèm lịch sử điểm) nên phải tải riêng.
  const [fetchedTrans, setFetchedTrans] = React.useState<PointTran[]>([]);
  const data = record.point_trans ?? fetchedTrans;

  React.useEffect(() => {
    if (record.point_trans || !record.id) return undefined;

    let ignore = false;
    getCustomerPointsHistoryApi(record.id)
      .then((rows) => {
        if (!ignore) setFetchedTrans(rows);
      })
      .catch((error: Error) => {
        if (!ignore) message.error(error.message || 'Không tải được lịch sử tích điểm');
      });

    return () => {
      ignore = true;
    };
  }, [record.id, record.point_trans]);

  const [orderCodeSearch, setOrderCodeSearch] = React.useState('');
  const [dateRange, setDateRange] = React.useState<[string | null, string | null]>([null, null]);

  const [recalculateOpen, setRecalculateOpen] = React.useState(false);
  const { can } = usePermissions();

  const { RangePicker } = DayjsDatePicker;

  const filteredData = React.useMemo(() => {
    return data.filter((item) => {
      const matchCode = orderCodeSearch ? item.order_code?.toLowerCase().includes(orderCodeSearch.toLowerCase()) : true;
      let matchDate = true;
      if (dateRange[0] && dateRange[1]) {
        const d = new Date(item.trans_date);
        matchDate = d >= new Date(dateRange[0]) && d <= new Date(dateRange[1]);
      }
      return matchCode && matchDate;
    });
  }, [data, orderCodeSearch, dateRange]);

  const handleExport = () => {
    const headers = [
      'Mã đơn hàng',
      'Thời gian',
      'Giá trị đơn hàng',
      'Điểm giao dịch',
      'Điểm hiện tại',
      'Ngày chuyển đổi',
    ];
    const rows = filteredData.map((item) => [
      item.order_code,
      formatDateTime(item.trans_date, 'date'),
      item.order_amount,
      item.point_number,
      item.available_point_number,
      item.converted_point_date ? formatDateTime(String(item.converted_point_date), 'date') : '',
    ]);
    const csvContent = [headers, ...rows].map((row) => row.map((val) => `"${val ?? ''}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `lich-su-tich-diem-${record.customer_code}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const columns: ColumnsType<PointTran> = [
    {
      title: 'Mã đơn hàng',
      dataIndex: 'order_code',
      key: 'order_code',
      render: (text, rec) =>
        rec.order_id ? (
          <Link to={`/orders/${rec.order_id}`} target="_blank">
            {text}
          </Link>
        ) : (
          '—'
        ),
    },
    {
      title: 'Thời gian',
      dataIndex: 'trans_date',
      key: 'trans_date',
      render: (value: any) => formatDateTime(value, 'date'),
    },
    {
      title: 'Giá trị đơn hàng',
      dataIndex: 'order_amount',
      key: 'order_amount',
      render: (value: any) => <NumberFormatRender value={value} />,
    },
    {
      title: 'Điểm giao dịch',
      dataIndex: 'point_number',
      key: 'point_number',
      // Số âm = điểm khách đã dùng trừ vào bill.
      render: (value: number, rec) => (
        <span className={`flex-center ${value < 0 ? 'text-danger' : 'text-primary'}`}>
          {value}
          {rec.kind === 'reserved' && (
            <Tag color="warning" className="tw-ml-2">
              Đang giữ
            </Tag>
          )}
          {rec.kind === 'expire' && <Tag className="tw-ml-2">Hết hạn</Tag>}
        </span>
      ),
    },
    {
      title: 'Điểm thưởng hiện tại',
      dataIndex: 'available_point_number',
      key: 'available_point_number',
      render: (value: any) => <span className="flex-center text-primary">{value}</span>,
    },
    {
      title: 'Ngày chuyển đổi',
      dataIndex: 'converted_point_date',
      key: 'converted_point_date',
      render: (value: any) => formatDateTime(value, 'date'),
    },
  ];

  return (
    <div className="px-3">
      <CustomerPointsSummary className="mb-3" summary={record.points_summary} pointValue={record.point_value} />
      <Space align="end" className="px-2 mb-3 flex-between" style={{ width: '100%' }}>
        {onPointsChanged && can('customers', 'update') ? (
          <Button icon={<ReloadOutlined />} onClick={() => setRecalculateOpen(true)}>
            Tính lại điểm thiếu
          </Button>
        ) : (
          <span />
        )}

        <Space wrap>
          <Input
            placeholder="Tìm mã đơn hàng"
            value={orderCodeSearch}
            onChange={(e) => setOrderCodeSearch(e.target.value)}
            allowClear
            size="small"
            style={{ width: 180, height: 36 }}
          />
          {/* Hiển thị dd-mm-yyyy nhưng state giữ ISO — bộ lọc bên trên so sánh bằng new Date(). */}
          <RangePicker
            format={DATE_FORMAT}
            size="small"
            value={dateRange[0] && dateRange[1] ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : [null, null]}
            onChange={(dates: any) =>
              setDateRange([dates?.[0]?.format(API_DATE_FORMAT) ?? null, dates?.[1]?.format(API_DATE_FORMAT) ?? null])
            }
            style={{ width: 220 }}
            ranges={dayjsPresetRanges()}
          />
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport} title="Xuất CSV">
            Export
          </Button>
        </Space>
      </Space>

      <BaseTable rowKey="id" columns={columns} dataSource={filteredData} size="small" />

      {recalculateOpen && onPointsChanged && (
        <RecalculatePointsModal
          open={recalculateOpen}
          customerId={record.id}
          onClose={() => setRecalculateOpen(false)}
          onDone={() => {
            setRecalculateOpen(false);
            onPointsChanged();
          }}
        />
      )}
    </div>
  );
};

export default PointsHistory;
